import { ROUTES } from '@/constants/routes';

import { SideNavItemProps } from './SideNavItem';

type NavItemsProps = {
  hasProPlan?: boolean;
  isAdminOrSupportRole?: boolean;
  isProductSupport?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  NavLink?: React.ComponentType<any>;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  entitlements: {
    hasApplicationsEntitlement?: boolean;
    hasEmployeeGroupsEntitlement?: boolean;
    hasAntivirusEntitlement?: boolean;
    hasEmailSecurityEntitlement?: boolean;
    hasGigawattEntitlement?: boolean;
    hasITPlanEntitlement?: boolean;
    hasElectricAppEntitlement?: boolean;
    hasEmployeeDataBreachEntitlement?: boolean;
    hasSupportCenterEntitlement?: boolean;
    hasHelpfulLinksEntitlement?: boolean;
    hasMarketplaceEntitlement?: boolean;
    hasJumpcloudMdmEntitlement?: boolean;
    hasXboardingsEntitlement?: boolean;
    hasWebhooksEntitlement?: boolean;
  };
  permissions: {
    canReadAssets?: boolean;
    canReadShipments?: boolean;
    canReadOrganizationPackage?: boolean;
    canReadOrganizationAddOns?: boolean;
    canReadWebhooks?: boolean;
  };
  hasHelpfulLinks?: boolean;
  showFreeTrial?: boolean;
  hasISVAddOns?: boolean;
  /** When provided, overrides visibility of the Gigawatt nav item (e.g. for gigawatt_basic) */
  showGigawattItem?: boolean;
  /** When true, show "Gigawatt" label in nav (gigawatt_basic-only orgs); otherwise show "Ask AI" */
  isGigawattBasicOnly?: boolean;
  flags?: Record<string, boolean>;
  t: (key: string) => string; // Add translation function
};

function getSecurityNavItem(
  /* eslint-disable @typescript-eslint/no-explicit-any */
  NavLink: React.ComponentType<any> | undefined,
  /* eslint-enable @typescript-eslint/no-explicit-any */
  hasAntivirusEntitlement: boolean | undefined,
  hasEmailSecurityEntitlement: boolean | undefined,
  hasEmployeeDataBreachEntitlement: boolean | undefined,
  isAdminOrSupportRole: boolean | undefined,
  hasMarketplaceEntitlement: boolean,
  hasDeviceManagement: boolean | undefined,
  t: (key: string) => string
): SideNavItemProps {
  const securitySubNavItems: SideNavItemProps[] = [];

  if (hasDeviceManagement) {
    // Show if device management FF is enabled & has Reading assets permission & has Electric app entitlement
    securitySubNavItems.push({
      text: t('nav.deviceManagement'),
      component: NavLink && <NavLink to="/device-management" />,
    });
  }

  if (hasAntivirusEntitlement) {
    securitySubNavItems.push({
      text: t('nav.antivirus'),
      component: NavLink && <NavLink to="/security-solutions/antivirus" />,
    });
  }

  if (hasEmailSecurityEntitlement) {
    securitySubNavItems.push({
      text: t('nav.emailSecurity'),
      component: NavLink && <NavLink to="/security-solutions/email-security" />,
    });
  }

  if (hasEmployeeDataBreachEntitlement) {
    securitySubNavItems.push({
      text: t('nav.passwordBreaches'),
      component: NavLink && <NavLink to="/password-breaches" />,
    });
  }

  const hasItems: boolean = securitySubNavItems.length > 0;

  if (hasMarketplaceEntitlement) {
    securitySubNavItems.push({
      text: t('nav.exploreSolutions'),
      component: NavLink && <NavLink to="/discover" end />,
    });
  }

  return {
    icon: 'shield-check',
    visible: isAdminOrSupportRole && hasItems,
    text: t('nav.security'),
    shortText: t('nav.securityShort'),
    subNavItems: securitySubNavItems,
  };
}

// TODO: Write a test for this - https://electricai.atlassian.net/browse/UIBB-2
export const generateNavItems = ({
  hasProPlan,
  isAdminOrSupportRole,
  isProductSupport,
  NavLink,
  entitlements,
  permissions,
  hasHelpfulLinks,
  hasISVAddOns,
  showFreeTrial = false,
  showGigawattItem: showGigawattItemProp,
  isGigawattBasicOnly,
  flags = {},
  t,
}: NavItemsProps): SideNavItemProps[] => {
  const {
    itHubShipmentQrCodeHistory,
    ithFeatureDescriptionPages,
    ithOutboundWebhooks,
    ithDivisions,
  } = flags;

  const {
    hasApplicationsEntitlement,
    hasEmployeeGroupsEntitlement,
    hasAntivirusEntitlement,
    hasEmailSecurityEntitlement,
    hasGigawattEntitlement,
    hasElectricAppEntitlement,
    hasEmployeeDataBreachEntitlement,
    hasSupportCenterEntitlement,
    hasHelpfulLinksEntitlement,
    hasMarketplaceEntitlement,
    hasJumpcloudMdmEntitlement,
    hasXboardingsEntitlement,
    hasWebhooksEntitlement,
  } = entitlements;
  const {
    canReadAssets,
    canReadShipments,
    canReadOrganizationPackage,
    canReadOrganizationAddOns,
    canReadWebhooks,
  } = permissions;

  const hasDeviceManagement =
    hasJumpcloudMdmEntitlement && canReadAssets && hasElectricAppEntitlement;

  // Check if user has ANY security-related entitlement
  const hasAnySecurityEntitlement =
    hasJumpcloudMdmEntitlement ||
    hasAntivirusEntitlement ||
    hasEmailSecurityEntitlement ||
    hasEmployeeDataBreachEntitlement ||
    hasMarketplaceEntitlement ||
    hasDeviceManagement;

  const securityMenuView = getSecurityNavItem(
    NavLink,
    hasAntivirusEntitlement,
    hasEmailSecurityEntitlement,
    hasEmployeeDataBreachEntitlement,
    isAdminOrSupportRole,
    !!hasMarketplaceEntitlement,
    hasDeviceManagement,
    t
  );

  const showSupportCenterItem = !!(hasSupportCenterEntitlement || hasISVAddOns);

  const showHelpfulLinksItem = !!(
    (hasHelpfulLinks || isAdminOrSupportRole) &&
    hasHelpfulLinksEntitlement
  );

  const shouldShowGigawattInSideNav =
    showGigawattItemProp !== undefined
      ? !!showGigawattItemProp
      : !!hasGigawattEntitlement;

  const gigawattItemText = isGigawattBasicOnly
    ? t('nav.gigawatt')
    : t('nav.askAI');

  const assetsMenuView: SideNavItemProps = {
    icon: 'laptop',
    text: t('nav.assets'),
    shortText: t('nav.assetsShort'),
    // Show if admin role
    visible: isAdminOrSupportRole,
    subNavItems: [
      // Show if assets FF is enabled & has Reading assets permission & admin role
      {
        text: t('nav.assetManagement'),
        visible: canReadAssets,
        component: NavLink && <NavLink to="/assets" />,
      },
      // Show if admin role
      {
        text: t('nav.getHardware'),
        component: NavLink && <NavLink to="/get-hardware" />,
      },
      // Show if admin role
      {
        text: t('nav.trackPurchaseRequests'),
        component: NavLink && <NavLink to="/track-orders" />,
      },
      // Show if admin role
      {
        text: t('nav.shipLaptop'),
        component: NavLink && <NavLink to="/ship-laptop" />,
      },
      // Show if admin role
      {
        text: t('nav.trackLaptopShipments'),
        visible: canReadShipments && itHubShipmentQrCodeHistory,
        component: NavLink && <NavLink to="/shipments/qr-code" />,
      },
    ],
  };
  return [
    /**
     * Defaults:
     * - Is not EUH
     * - Internal pages are hidden
     * - Does not have employee groups entitlement
     * - Is admin role
     * - Does not have helpful links
     */
    /* ---- IT Management Nav Items ---- */
    {
      // In ITH: Show if admin role
      // In EUH: Show if admin role
      sectionHeading: t('nav.adminCenter'),
      visible: isAdminOrSupportRole,
    },
    {
      icon: 'home',
      text: t('nav.dashboard'),
      shortText: t('nav.dashboardShort'),
      visible: isAdminOrSupportRole,
      component: NavLink && <NavLink to="/dashboard" />,
    },
    /**
     * If the organization has a Pro plan, show the security menu view first,
     * otherwise show the assets menu view first.
     */
    ...(hasProPlan
      ? [securityMenuView, assetsMenuView]
      : [assetsMenuView, securityMenuView]),
    {
      // In ITH: Show if admin role
      // In EUH: Show if admin role
      icon: 'user-group',
      text: t('nav.people'),
      shortText: t('nav.peopleShort'),
      visible: isAdminOrSupportRole,
      subNavItems: [
        {
          text: t('nav.employees'),
          component: NavLink && <NavLink to="/employees" />,
        },
        {
          // Only show if user has the employee groups entitlement.
          text: t('nav.groups'),
          visible: hasEmployeeGroupsEntitlement,
          component: NavLink && <NavLink to="/groups" />,
          ...(showFreeTrial && { chipText: 'Free trial' }),
        },
      ],
    },
    {
      // In ITH: Show if hide applications FF is disabled (default) & admin role
      // In EUH: Show if hide applications FF is disabled (default) & admin role
      // Hide this item if ithFeatureDescriptionPages is enabled and user doesn't have entitlement
      // (it will show under Available Upgrades section instead)
      icon: 'app',
      text: t('nav.applications'),
      shortText: t('nav.applicationsShort'),
      visible:
        isAdminOrSupportRole &&
        (!ithFeatureDescriptionPages ||
          hasApplicationsEntitlement ||
          !!isProductSupport),
      component: NavLink && <NavLink to="/applications" />,
      ...(showFreeTrial && { chipText: 'Free trial' }),
    },

    /* ---- Settings Nav Items ---- */
    {
      icon: 'cog',
      text: t('nav.settings'),
      shortText: t('nav.settingsShort'),
      visible: isAdminOrSupportRole,
      subNavItems: [
        {
          text: t('nav.settingsCompanyProfile'),
          component: NavLink && <NavLink to="/settings/company" />,
        },
        {
          text: t('nav.settingsDeviceManagement'),
          component: NavLink && <NavLink to="/settings/device" />,
        },
        {
          text: t('nav.settingsBilling'),
          visible: !!(canReadOrganizationPackage || canReadOrganizationAddOns),
          component: NavLink && <NavLink to="/settings/billing" />,
        },
        {
          text: t('nav.settingsSupportCenter'),
          visible: !!(hasXboardingsEntitlement || hasSupportCenterEntitlement),
          component: NavLink && <NavLink to="/settings/category-champions" />,
        },
        {
          text: t('nav.settingsIntegrations'),
          component: NavLink && <NavLink to="/settings/integrations" />,
        },
        {
          text: t('nav.settingsWebhooks'),
          visible: !!(
            ithOutboundWebhooks &&
            hasWebhooksEntitlement &&
            canReadWebhooks
          ),
          component: NavLink && <NavLink to="/settings/webhooks" />,
        },
        {
          text: t('nav.settingsDivisions'),
          visible: !!ithDivisions,
          component: NavLink && <NavLink to="/settings/divisions" />,
        },
      ],
    },

    /* ---- Available Upgrades Section ---- */
    {
      sectionHeading: t('nav.availableUpgrades'),
      visible:
        isAdminOrSupportRole &&
        ithFeatureDescriptionPages &&
        ((!hasApplicationsEntitlement && !isProductSupport) ||
          !hasJumpcloudMdmEntitlement ||
          !hasAnySecurityEntitlement),
    },
    {
      icon: 'app',
      text: t('nav.applications'),
      shortText: t('nav.applicationsShort'),
      visible:
        isAdminOrSupportRole &&
        ithFeatureDescriptionPages &&
        !hasApplicationsEntitlement &&
        !isProductSupport,
      component: NavLink && <NavLink to="/applications" />,
    },
    {
      icon: 'shield-check',
      text: t('nav.security'),
      shortText: t('nav.securityShort'),
      // Show if: user lacks MDM (upgrade to MDM) OR lacks any security (upgrade to security)
      visible:
        isAdminOrSupportRole &&
        ithFeatureDescriptionPages &&
        (!hasJumpcloudMdmEntitlement || !hasAnySecurityEntitlement),
      component: NavLink && <NavLink to="/security" />,
    },

    /* ---- End User Hub Nav Items ---- */
    {
      // In ITH: Show if admin role
      // In EUH: Show if admin role
      sectionHeading: t('nav.employeeCenter'),
      visible:
        isAdminOrSupportRole &&
        (showSupportCenterItem ||
          showHelpfulLinksItem ||
          shouldShowGigawattInSideNav),
    },
    {
      icon: 'messages-question',
      text: t('nav.supportCenter'),
      shortText: t('nav.supportCenterShort'),
      visible: showSupportCenterItem,
      component: NavLink && <NavLink to="/support-center" />,
    },
    {
      icon: 'applications',
      text: t('nav.helpfulLinks'),
      shortText: t('nav.helpfulLinksShort'),
      visible: showHelpfulLinksItem,
      component: NavLink && <NavLink to="/helpful-links" />,
    },
    {
      icon: 'sparkles',
      text: gigawattItemText,
      shortText: isGigawattBasicOnly
        ? t('nav.gigawattShort')
        : t('nav.askAIShort'),
      visible: shouldShowGigawattInSideNav,
      component: NavLink && <NavLink to={ROUTES.AI_CHAT} />,
    },
  ];
};
